import React, { ReactNode } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'

import TabsMenu from 'modules/core/components/Card/CardTabsMenu'
import type { Tab } from 'modules/core/components/Card/types'
import i18n from 'modules/core/lib/i18n'

import Actions, { Action, Option } from './_Action'
import PrivateTag, { TagProps as PrivateTagProps } from './_Tag'
import * as styles from './index.vanilla.css'

interface Props {
  action?: Action
  options?: Option[]
  optionsButton?: ReactNode
  backTo?: string
  onBackClick?: () => void
  onClose?: () => void
}

const Header = ({
  action,
  options,
  onClose,
  optionsButton,
  backTo,
  onBackClick,
  children,
}: React.PropsWithChildren<Props>) => {
  return (
    <Box className={styles.header}>
      {backTo ? (
        <Box aria-label={i18n.t('common.back')} className={styles.left}>
          <Button.Router type="secondary" to={backTo} onClick={onBackClick} iconName="arrow-left" />
        </Box>
      ) : null}
      <Box justifyContent="center" alignItems="center" width="full">
        {children}
      </Box>
      <Actions action={action} options={options} optionsButton={optionsButton} />
      {onClose ? (
        <Box aria-label={i18n.t('common.close')} className={styles.right}>
          <Button type="secondary" onClick={onClose} iconName="cross" />
        </Box>
      ) : null}
    </Box>
  )
}

interface PrivateTitleProps extends Props {
  label: string
  children?: React.ReactNode
}

const PrivateTitle = ({
  action,
  options,
  optionsButton,
  backTo,
  onBackClick,
  label,
  children,
}: PrivateTitleProps) => {
  return (
    <Header
      action={action}
      options={options}
      optionsButton={optionsButton}
      backTo={backTo}
      onBackClick={onBackClick}
    >
      <Box flexDirection="row" gap="s8">
        <div title={label}>
          <Box overflowX="hidden" maxWidth="s400" alignItems="center">
            <Text.Normal weight="semibold" ellipsis>
              {label}
            </Text.Normal>
          </Box>
        </div>
        {children}
      </Box>
    </Header>
  )
}

interface TitleProps extends Props {
  children: string
}

const Title = ({ children, ...props }: TitleProps) => {
  return <PrivateTitle label={children} {...props} />
}

interface TagProps extends Props {
  children: string
  tag: PrivateTagProps
}

const Tag = ({ children, tag, ...props }: TagProps) => {
  return (
    <PrivateTitle label={children} {...props}>
      <PrivateTag {...tag}></PrivateTag>
    </PrivateTitle>
  )
}

interface TabsProps extends Props {
  tabs: Tab[]
}

const Tabs = ({ action, options, optionsButton, backTo, onBackClick, tabs }: TabsProps) => {
  return (
    <Header
      action={action}
      options={options}
      optionsButton={optionsButton}
      backTo={backTo}
      onBackClick={onBackClick}
    >
      {tabs.length > 0 && <TabsMenu tabs={tabs} />}
    </Header>
  )
}

Header.Title = Title
Header.Tag = Tag
Header.Tabs = Tabs

export default Header
